import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import {
  createTheme,
  ThemeProvider,
  CssBaseline,
  useMediaQuery,
  GlobalStyles,
  alpha,
  PaletteMode,
} from '@mui/material';

/**
 * Design system: "liquid glass".
 *
 * Every surface (Card/Paper/AppBar/Drawer/Dialog) is a frosted translucent
 * panel (backdrop-filter blur + saturate) floating over a soft gradient page
 * background. Works in light and dark; the mode can be forced or follow the OS
 * ("auto"). The choice is persisted in localStorage.
 */

export type ThemeMode = 'light' | 'dark' | 'auto';

interface ThemeModeContextValue {
  mode: ThemeMode; // what the user picked (may be "auto")
  resolvedMode: PaletteMode; // what's actually rendered (light|dark)
  setMode: (mode: ThemeMode) => void;
  cycleMode: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

export function useThemeMode(): ThemeModeContextValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error('useThemeMode must be used within ThemeModeProvider');
  return ctx;
}

const STORAGE_KEY = 'themeMode';

function readStoredMode(): ThemeMode {
  const v = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  return v === 'light' || v === 'dark' || v === 'auto' ? v : 'auto';
}

function createAppTheme(mode: PaletteMode) {
  const isLight = mode === 'light';

  // Glass surface colours per mode
  const glassBg = isLight ? 'rgba(255, 255, 255, 0.62)' : 'rgba(24, 28, 46, 0.55)';
  const glassBorder = isLight ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.09)';
  const glassShadow = isLight
    ? '0 8px 32px rgba(31, 41, 92, 0.10)'
    : '0 8px 32px rgba(0, 0, 0, 0.45)';
  const glassBlur = 'blur(20px) saturate(180%)';

  const theme = createTheme({
    palette: {
      mode,
      primary: { main: '#5468ff', light: '#7d8bff', dark: '#3a4bd6', contrastText: '#fff' },
      secondary: { main: '#9b6bff' },
      success: { main: '#12b76a' },
      warning: { main: '#f79009' },
      error: { main: '#f04438' },
      background: {
        default: isLight ? '#eef1fb' : '#0d1020',
        paper: glassBg,
      },
      text: {
        primary: isLight ? '#1a2138' : '#eef1fb',
        secondary: isLight ? '#5a6485' : '#a6b0d4',
      },
      divider: isLight ? 'rgba(31,41,92,0.10)' : 'rgba(255,255,255,0.10)',
    },
    shape: { borderRadius: 16 },
    typography: {
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      h4: { fontWeight: 700, letterSpacing: '-0.02em' },
      h5: { fontWeight: 700, letterSpacing: '-0.01em' },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: glassBg,
            backdropFilter: glassBlur,
            WebkitBackdropFilter: glassBlur,
            border: `1px solid ${glassBorder}`,
            boxShadow: glassShadow,
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: glassBg,
            backdropFilter: glassBlur,
            WebkitBackdropFilter: glassBlur,
            border: `1px solid ${glassBorder}`,
            boxShadow: glassShadow,
            borderRadius: 18,
            transition: 'transform .25s ease, box-shadow .25s ease',
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'default' },
        styleOverrides: {
          root: {
            backgroundColor: isLight ? 'rgba(255,255,255,0.55)' : 'rgba(16,19,36,0.55)',
            backdropFilter: glassBlur,
            WebkitBackdropFilter: glassBlur,
            borderBottom: `1px solid ${glassBorder}`,
            color: isLight ? '#1a2138' : '#eef1fb',
            boxShadow: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isLight ? 'rgba(255,255,255,0.5)' : 'rgba(16,19,36,0.5)',
            backdropFilter: glassBlur,
            WebkitBackdropFilter: glassBlur,
            borderRight: `1px solid ${glassBorder}`,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 12, paddingInline: 18 },
          containedPrimary: {
            background: 'linear-gradient(135deg, #5468ff 0%, #7d5bff 100%)',
            boxShadow: '0 6px 18px rgba(84,104,255,0.35)',
            '&:hover': {
              background: 'linear-gradient(135deg, #3f54f0 0%, #6a49f0 100%)',
              boxShadow: '0 8px 22px rgba(84,104,255,0.45)',
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            margin: '2px 8px',
            '&.Mui-selected': {
              backgroundColor: alpha('#5468ff', isLight ? 0.12 : 0.24),
              '&:hover': { backgroundColor: alpha('#5468ff', isLight ? 0.18 : 0.3) },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: { root: { fontWeight: 500 } },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: isLight ? 'rgba(255,255,255,0.85)' : 'rgba(22,26,44,0.9)',
            backdropFilter: 'blur(24px) saturate(180%)',
            WebkitBackdropFilter: 'blur(24px) saturate(180%)',
          },
        },
      },
      MuiTextField: { defaultProps: { variant: 'outlined' } },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { borderRadius: 8, backdropFilter: 'blur(8px)' },
        },
      },
    },
  });

  return theme;
}

/**
 * Soft animated gradient page background — the colourful layer the glass
 * surfaces blur and refract. Defined as global body styles so it sits behind
 * everything and covers the whole viewport including overscroll.
 */
function backgroundStyles(mode: PaletteMode) {
  const light = {
    background:
      'radial-gradient(1200px 800px at 8% 2%, #e0e7ff 0%, transparent 55%),' +
      'radial-gradient(1000px 700px at 95% 10%, #ede0ff 0%, transparent 50%),' +
      'radial-gradient(900px 900px at 60% 100%, #d9f6ff 0%, transparent 55%),' +
      'linear-gradient(180deg, #eef1fb 0%, #f4f0ff 100%)',
  };
  const dark = {
    background:
      'radial-gradient(1200px 800px at 8% 2%, #1e2450 0%, transparent 55%),' +
      'radial-gradient(1000px 700px at 95% 10%, #2a1e50 0%, transparent 50%),' +
      'radial-gradient(900px 900px at 60% 100%, #0e2a3a 0%, transparent 55%),' +
      'linear-gradient(180deg, #0d1020 0%, #12132a 100%)',
  };
  const bg = mode === 'light' ? light : dark;
  return {
    'html, body, #root': { minHeight: '100%' },
    body: {
      ...bg,
      backgroundAttachment: 'fixed',
      backgroundRepeat: 'no-repeat',
    },
  };
}

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const systemPrefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  const resolvedMode: PaletteMode =
    mode === 'auto' ? (systemPrefersDark ? 'dark' : 'light') : mode;

  const setMode = (m: ThemeMode) => {
    setModeState(m);
    localStorage.setItem(STORAGE_KEY, m);
  };
  const cycleMode = () => {
    const order: ThemeMode[] = ['light', 'dark', 'auto'];
    setMode(order[(order.indexOf(mode) + 1) % order.length]);
  };

  const theme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode]);

  // Expose resolved mode on <html data-theme> for any non-MUI CSS (e.g. toasts)
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedMode);
  }, [resolvedMode]);

  const ctx = useMemo(
    () => ({ mode, resolvedMode, setMode, cycleMode }),
    [mode, resolvedMode]
  );

  return (
    <ThemeModeContext.Provider value={ctx}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles styles={backgroundStyles(resolvedMode)} />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

/** Reusable keyframes for the "config is working" pulse/glow. */
export const workingPulseSx = {
  animation: 'vkbpWorkingPulse 1.8s ease-in-out infinite',
  '@keyframes vkbpWorkingPulse': {
    '0%, 100%': { boxShadow: '0 0 0 0 rgba(84,104,255,0.0), 0 8px 32px rgba(31,41,92,0.10)' },
    '50%': { boxShadow: '0 0 0 4px rgba(84,104,255,0.18), 0 8px 32px rgba(84,104,255,0.30)' },
  },
} as const;
